/**
 * component-product-form.js
 * <variant-picker>, <sticky-atc>, and AJAX add-to-cart.
 *
 * Variant state is resolved entirely client-side from the option list embedded
 * by variant-picker.liquid. The plan requires the price to update without a
 * spinner, and a Section Rendering round-trip per option tap cannot do that.
 * The cost is that money formatting happens in JS — Theme.formatMoney mirrors
 * Shopify's money_format exactly for that reason.
 */
(function () {
  'use strict';

  // Single-execution guard — see component-drawer.js. The add-to-cart submit
  // handler is bound at document level; a duplicate would POST /cart/add twice
  // and put two of the same item in the cart.
  if (window.__themeProductFormLoaded) return;
  window.__themeProductFormLoaded = true;

  // <quantity-input> now lives in component-quantity.js — the cart drawer and
  // cart page use it too, and this file does not load there.

  /* ==================================================================== *
   * <variant-picker>
   * ==================================================================== */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-variant-data]');
      if (!dataEl) return;

      try {
        this.variants = JSON.parse(dataEl.textContent);
      } catch (error) {
        console.error('[theme] Invalid variant data', error);
        return;
      }

      this.select = this.querySelector('[data-variant-select]');
      this.addEventListener('change', this.onChange.bind(this));
    }

    /** Current selection, read from the checked radio in each fieldset. */
    getSelectedOptions() {
      return Array.from(this.querySelectorAll('.variant-picker__group')).map((group) => {
        const checked = group.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onChange(event) {
      if (!event.target.matches('.variant-picker__input')) return;

      const selected = this.getSelectedOptions();

      // Every option must match. `every` over the variant's own options array
      // handles 1, 2 and 3-option products without special-casing.
      const match = this.variants.find((variant) =>
        variant.options.every((value, index) => value === selected[index])
      );

      this.updateLegend(event.target);
      this.updateAvailability(selected);

      if (!match) {
        this.setUnavailable();
        return;
      }

      this.updateUrl(match.id);
      this.updateSelect(match.id);
      this.updatePrice(match);
      this.updateButton(match);
      this.updateGallery(match);

      window.Theme.emit('variant:changed', match);
    }

    /** Shows the chosen value beside the option name. */
    updateLegend(input) {
      const group = input.closest('.variant-picker__group');
      const label = group && group.querySelector('[data-selected-value]');
      if (label) label.textContent = input.value;
    }

    /**
     * Marks option values that cannot combine with the current selection.
     *
     * Disabled, never hidden: options vanishing as you choose makes the page
     * feel broken and hides which combinations exist. A value stays enabled if
     * ANY available variant contains it alongside the other current choices.
     */
    updateAvailability(selected) {
      this.querySelectorAll('.variant-picker__group').forEach((group, groupIndex) => {
        group.querySelectorAll('.variant-picker__input').forEach((input) => {
          const candidate = selected.slice();
          candidate[groupIndex] = input.value;

          const exists = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options.every((value, index) => index === groupIndex || value === candidate[index]) &&
              variant.options[groupIndex] === input.value
          );

          input.disabled = !exists;
        });
      });
    }

    updateUrl(variantId) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      // replaceState, not pushState: option changes are refinement, not
      // navigation. Pushing would require one Back press per option tapped.
      window.history.replaceState({}, '', url.toString());
    }

    updateSelect(variantId) {
      if (this.select) this.select.value = variantId;
    }

    updatePrice(variant) {
      const container = document.querySelector('[data-price-container]');
      if (!container) return;

      const current = container.querySelector('.price__current');
      const compare = container.querySelector('.price__compare');
      const priceRoot = container.querySelector('.price');

      if (current) current.textContent = window.Theme.formatMoney(variant.price);

      const onSale = variant.compare_at_price > variant.price;
      if (priceRoot) priceRoot.classList.toggle('price--on-sale', onSale);

      if (compare) {
        compare.textContent = onSale ? window.Theme.formatMoney(variant.compare_at_price) : '';
        compare.hidden = !onSale;
      }

      const sticky = document.querySelector('[data-sticky-price] .price__current');
      if (sticky) sticky.textContent = window.Theme.formatMoney(variant.price);

      window.Theme.announce(
        window.Theme.strings.priceUpdated
          ? window.Theme.strings.priceUpdated.replace('{{ price }}', window.Theme.formatMoney(variant.price))
          : ''
      );
    }

    updateButton(variant) {
      document.querySelectorAll('[data-add-button], [data-sticky-add]').forEach((button) => {
        button.disabled = !variant.available;
        const label = button.querySelector('[data-add-label]') || button;
        label.textContent = variant.available
          ? window.Theme.strings.addToCart
          : window.Theme.strings.soldOut;
      });

      const meta = document.querySelector('[data-sticky-meta]');
      if (meta) meta.textContent = this.getSelectedOptions().filter(Boolean).join(' · ');
    }

    updateGallery(variant) {
      if (!variant.media_id) return;
      const gallery = document.querySelector('[data-gallery]');
      if (gallery && typeof gallery.showMedia === 'function') gallery.showMedia(variant.media_id);
    }

    /** No variant matches the chosen combination. */
    setUnavailable() {
      document.querySelectorAll('[data-add-button], [data-sticky-add]').forEach((button) => {
        button.disabled = true;
        const label = button.querySelector('[data-add-label]') || button;
        label.textContent = window.Theme.strings.variantUnavailable;
      });
    }
  }

  /* ==================================================================== *
   * <sticky-atc>
   * Revealed once the real add-to-cart button leaves the viewport.
   * ==================================================================== */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const target = document.querySelector('[data-add-button]');
      if (!target || !('IntersectionObserver' in window)) return;

      // Observing the real button is cheaper and more accurate than a scroll
      // listener, and it stays correct when content above it changes height.
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
        },
        { rootMargin: '0px 0px -80px 0px' }
      );

      this.observer.observe(target);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  /* ==================================================================== *
   * Add to cart
   * ==================================================================== */

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;

    event.preventDefault();

    const buttons = document.querySelectorAll('[data-add-button], [data-sticky-add]');
    const errorEl = form.querySelector('[data-add-error]');

    // Captured BEFORE the buttons are disabled below. Disabling the focused
    // button blurs it, so by the time the drawer opens document.activeElement
    // is <body> — and the drawer's focus-restore-on-close then sends a
    // keyboard user to the top of the document instead of back to the control
    // they pressed. The drawer needs to be told explicitly.
    const submitter = event.submitter || form.querySelector('[data-add-button]');

    buttons.forEach((button) => {
      button.classList.add('is-loading');
      // Disabling prevents the double-submit that creates duplicate lines when
      // a customer taps twice on a slow connection.
      button.disabled = true;
    });

    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    const body = new FormData(form);
    // Ask the cart API to return the drawer's section markup in the same
    // round-trip, so adding to cart is one request rather than two.
    const drawer = document.getElementById('CartDrawer');
    if (drawer && drawer.dataset.sectionId) {
      body.append('sections', drawer.dataset.sectionId);
    }

    fetch(window.Theme.routes.cart_add, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          // Shopify returns a human-readable `description` for cart errors —
          // "You can only add 3 of this item" and similar. Surfacing it beats
          // a generic failure message.
          throw new Error(data.description || data.message || window.Theme.strings.cartError);
        }

        // `openerElement` rides along on the payload so the cart drawer can
        // return focus to the exact control that opened it. It is a DOM node,
        // not cart data — consumers must treat it as optional.
        data.openerElement = submitter;
        window.Theme.emit('cart:added', data);
        window.Theme.announce(window.Theme.strings.addedToCart);
      })
      .catch((error) => {
        if (errorEl) {
          errorEl.textContent = error.message;
          errorEl.hidden = false;
        } else {
          window.Theme.announce(error.message);
        }
      })
      .finally(() => {
        buttons.forEach((button) => {
          button.classList.remove('is-loading');
          button.disabled = false;
        });
      });
  });

  /* ==================================================================== *
   * <product-story>
   * Clamps a long imported description and reveals it on request.
   *
   * The copy is rendered in full by Liquid and stays in the DOM at all
   * times — this only limits its HEIGHT. Nothing is removed, so in-page
   * search, crawlers and assistive tech all still see the whole thing.
   * ==================================================================== */

  class ProductStory extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('[data-story-body]');
      this.toggle = this.querySelector('[data-story-toggle]');
      this.label = this.querySelector('[data-story-toggle-label]');
      if (!this.body || !this.toggle) return;

      // Clamping copy that barely exceeds the limit costs the customer a click
      // to reveal a couple of lines. Only worth it when there is real bulk
      // hidden behind the control.
      const clamp = parseFloat(getComputedStyle(this.body).fontSize) * 22;
      if (this.body.scrollHeight <= clamp * 1.25) return;

      this.setAttribute('data-collapsed', '');
      this.toggle.hidden = false;
      this.body.id = this.body.id || 'ProductStoryBody';
      this.toggle.setAttribute('aria-controls', this.body.id);

      this.onClick = this.onClick.bind(this);
      this.toggle.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      if (this.toggle && this.onClick) this.toggle.removeEventListener('click', this.onClick);
    }

    onClick() {
      const collapsed = this.hasAttribute('data-collapsed');

      if (collapsed) {
        this.removeAttribute('data-collapsed');
      } else {
        this.setAttribute('data-collapsed', '');
        // Collapsing from below the fold would leave the customer staring at
        // whatever happened to be under the band, with no idea the page moved.
        const top = this.getBoundingClientRect().top;
        if (top < 0) this.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }

      this.toggle.setAttribute('aria-expanded', String(collapsed));
      if (this.label) {
        this.label.textContent = collapsed
          ? window.Theme.strings.readLess
          : window.Theme.strings.readMore;
      }
    }
  }

  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);
  if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);
  if (!customElements.get('product-story')) customElements.define('product-story', ProductStory);
})();
